import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListSkeleton } from "../components/Skeletons";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, UserPlus, Loader2, X, Check, Trash2, Shield, Crown, Wrench, CheckCircle2, Filter, Search, Settings2, KeyRound, Pencil } from "lucide-react";
import PageHeader from "../components/PageHeader";
import MemberHoverCard from "../components/team/MemberHoverCard";
import { TENANT_ROLES, BUSINESS_ROLES, BUSINESS_LABELS, BUSINESS_COLORS } from "../constants/roleConstants";

export default function TenantTeamPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "tenant_member", password: "", business_roles: [], poste: "", poste_id: "" });
  const [posteMode, setPosteMode] = useState("");
  const [postes, setPostes] = useState([]);
  const [editPoste, setEditPoste] = useState(null); // { userId, poste, preset }
  const [posteSaving, setPosteSaving] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [error, setError] = useState("");
  // Filtres
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterBusiness, setFilterBusiness] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tenant/members");
      setMembers(r.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.get("/tenant/postes").then(r => setPostes(r.data?.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMember = async () => {
    setAddLoading(true);
    setError("");
    try {
      await api.post("/tenant/members", form);
      setShowAdd(false);
      setForm({ email: "", name: "", role: "tenant_member", password: "", business_roles: [], poste: "", poste_id: "" });
      setPosteMode("");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur");
    }
    setAddLoading(false);
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    try {
      await api.delete(`/tenant/members/${userId}`);
      load();
    } catch {}
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.put(`/tenant/members/${userId}/role`, { role: newRole });
      load();
    } catch {}
  };

  const toggleEquipements = async (userId, current) => {
    try {
      await api.put(`/tenant/members/${userId}/access-equipements`, { access_equipements: !current });
      load();
    } catch {}
  };

  const savePoste = async (member) => {
    if (!editPoste) return;
    setPosteSaving(true);
    try {
      if (editPoste.poste_id) {
        // Poste paramétré → rattachement + application des droits du poste
        await api.post(`/tenant/members/${member.user_id}/poste/assign`, { poste_id: editPoste.poste_id });
      } else {
        await api.put(`/tenant/members/${member.user_id}/poste`, { poste: editPoste.poste });
      }
      setEditPoste(null);
      load();
    } catch {}
    setPosteSaving(false);
  };

  const tenant = user?.tenant;
  const maxMembers = tenant?.max_members || 5;
  const teamCount = members.filter(m => m.role === "tenant_admin" || m.role === "tenant_member").length;

  if (loading) return <div className="p-6 lg:p-10" data-testid="team-loading"><ListSkeleton items={5} /></div>;

  return (
    <div className="p-6 max-w-[900px] mx-auto" data-testid="tenant-team-page">
      <PageHeader
        icon={<Users />}
        title="Mon équipe"
        description={`${teamCount} / ${maxMembers} membres d'equipe - ${members.length - teamCount} utilisateur(s)`}
        testId="tenant-team-header"
        actions={(
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/tenant/team/access")} variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid="manage-access-btn">
              <KeyRound className="w-4 h-4 mr-1" /> Gestion des accès
            </Button>
            <Button variant="outline" onClick={() => navigate("/tenant/postes")} className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="manage-postes-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Gérer les postes
            </Button>
            <Button onClick={() => setShowAdd(true)} disabled={teamCount >= maxMembers} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="add-member-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        )}
      />

      {/* Filtres */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Rechercher un membre..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-[#0b0811] border-white/10 text-white text-sm" data-testid="tenant-team-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs min-w-[160px]" data-testid="tenant-filter-role-select">
              <SelectValue placeholder="Type d'acces" />
            </SelectTrigger>
            <SelectContent className="bg-[#14101c] border-white/10 text-white">
              <SelectItem value="all">Tous les types d'acces</SelectItem>
              {TENANT_ROLES.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />{r.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterBusiness} onValueChange={setFilterBusiness}>
            <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs min-w-[160px]" data-testid="tenant-filter-business-select">
              <SelectValue placeholder="Role metier" />
            </SelectTrigger>
            <SelectContent className="bg-[#14101c] border-white/10 text-white">
              <SelectItem value="all">Tous les roles metier</SelectItem>
              {BUSINESS_ROLES.map(b => (
                <SelectItem key={b.value} value={b.value}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />{b.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filterRole !== "all" || filterBusiness !== "all" || search) && (
            <button onClick={() => { setFilterRole("all"); setFilterBusiness("all"); setSearch(""); }} className="text-[10px] px-2 py-1 rounded text-slate-400 hover:text-white" data-testid="tenant-filter-reset">Reinit.</button>
          )}
        </div>
      </div>

      {/* Members List */}
      <div className="space-y-3">
        {members
          .filter(m => {
            if (filterRole !== "all" && m.role !== filterRole) return false;
            if (filterBusiness !== "all" && !(m.roles || []).includes(filterBusiness)) return false;
            if (search && !(m.name || m.email || "").toLowerCase().includes(search.toLowerCase())) return false;
            return true;
          })
          .map(m => (
          <MemberHoverCard key={m.user_id} userId={m.user_id} disabled={m.user_id === user?.user_id}>
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-4 hover:border-cyan-400/30 hover:bg-[#1a1626] transition-colors cursor-pointer" data-testid={`team-member-${m.user_id}`}
               onClick={() => navigate(`/tenant/team/${m.user_id}`)}>
            <div className="w-12 h-12 rounded-full bg-[#0b0811] flex items-center justify-center text-lg font-bold text-[#f7c935]">
              {(m.name || m.email || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white">{m.name}</div>
              <div className="text-sm text-slate-400">{m.email}</div>
              {editPoste?.userId === m.user_id ? (
                <div className="flex items-center gap-1.5 mt-1.5" onClick={e => e.stopPropagation()} data-testid={`poste-editor-${m.user_id}`}>
                  <select
                    value={editPoste.poste_id || (editPoste.custom ? "__custom__" : "")}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === "__custom__") setEditPoste({ ...editPoste, poste_id: "", custom: true });
                      else if (v === "") setEditPoste({ ...editPoste, poste_id: "", poste: "", custom: false });
                      else {
                        const p = postes.find(x => x.poste_id === v);
                        setEditPoste({ ...editPoste, poste_id: v, poste: p?.name || v, custom: false });
                      }
                    }}
                    className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-[11px] text-white"
                    data-testid={`poste-edit-select-${m.user_id}`}>
                    <option value="">— Aucun poste —</option>
                    {postes.map(p => (
                      <option key={p.poste_id} value={p.poste_id}>{p.name} ({(p.grants || []).length} droits)</option>
                    ))}
                    <option value="__custom__">Autre (libre)</option>
                  </select>
                  {editPoste.custom && (
                    <input
                      value={editPoste.poste}
                      onChange={e => setEditPoste({ ...editPoste, poste: e.target.value })}
                      placeholder="Intitulé"
                      className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-[11px] text-white w-28"
                      data-testid={`poste-edit-input-${m.user_id}`}
                    />
                  )}
                  <button onClick={() => savePoste(m)} disabled={posteSaving} className="p-1 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid={`poste-edit-save-${m.user_id}`}>
                    {posteSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  </button>
                  <button onClick={() => setEditPoste(null)} className="p-1 rounded bg-white/5 text-slate-400 hover:bg-white/10" data-testid={`poste-edit-cancel-${m.user_id}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-0.5">
                  {m.poste ? (
                    <span className="text-[11px] font-semibold" style={{ color: "#38bdf8" }} data-testid={`member-poste-${m.user_id}`}>
                      {m.poste}
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-600">Aucun poste</span>
                  )}
                  {m.role === "tenant_member" && (
                    <button
                      onClick={e => { e.stopPropagation(); const known = postes.find(p => p.name === m.poste); setEditPoste({ userId: m.user_id, poste: m.poste || "", poste_id: known?.poste_id || "", custom: !!m.poste && !known }); }}
                      className="p-0.5 rounded text-slate-500 hover:text-white hover:bg-white/10"
                      title="Modifier le poste"
                      data-testid={`poste-edit-btn-${m.user_id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
              {(m.roles || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(m.roles || []).map(rk => (
                    <span key={rk} className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${BUSINESS_COLORS[rk] || "#666"}20`, color: BUSINESS_COLORS[rk] || "#999" }}>
                      {BUSINESS_LABELS[rk] || rk}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {m.role === "client" ? (
              <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#8b5cf6]/10 text-[#8b5cf6]" data-testid={`role-badge-${m.user_id}`}>Utilisateur</span>
            ) : (
              <select value={m.role} onChange={e => changeRole(m.user_id, e.target.value)} disabled={m.user_id === user?.user_id}
                onClick={e => e.stopPropagation()}
                className="bg-[#0b0811] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid={`role-select-${m.user_id}`}>
                <option value="tenant_admin">Admin</option>
                <option value="tenant_member">Salarié</option>
              </select>
            )}
            <button onClick={e => { e.stopPropagation(); toggleEquipements(m.user_id, m.access_equipements); }} title={m.access_equipements ? "Desactiver acces equipements" : "Activer acces equipements"}
              className={`p-2 rounded-lg transition-colors ${m.access_equipements ? "bg-[#f7c935]/10 text-[#f7c935]" : "bg-white/5 text-slate-600 hover:bg-white/10"}`}
              data-testid={`toggle-equipements-${m.user_id}`}>
              <Wrench className="w-4 h-4" />
            </button>
            <span className="text-xs text-cyan-400 font-bold flex items-center gap-1 px-2 py-1 rounded bg-cyan-500/10">
              Voir le profil →
            </span>
            {m.user_id !== user?.user_id && (
              <button onClick={e => { e.stopPropagation(); removeMember(m.user_id); }} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`remove-member-${m.user_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {m.user_id === user?.user_id && (
              <span className="text-[10px] text-[#f7c935] font-bold flex items-center gap-1"><Crown className="w-3 h-3" /> Vous</span>
            )}
          </div>
          </MemberHoverCard>
        ))}
      </div>

      {/* Add Member Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="add-member-modal">
          <div className="bg-[#0b0811] border border-white/10 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Ajouter un membre</h3>
              <button onClick={() => { setShowAdd(false); setError(""); }} className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {error && <div className="text-red-400 text-sm mb-3 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Email *</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="new-member-email" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Nom</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="new-member-name" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Mot de passe temporaire (défaut : changeme123)</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="new-member-password" />
                <p className="text-[10px] text-slate-500 mt-1">Le membre devra choisir son propre mot de passe à sa première connexion.</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Type d'accès</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="new-member-role-select">
                  {TENANT_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
                  ))}
                </select>
              </div>
              {form.role === "tenant_member" && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Poste (applique les droits du poste en 1 clic)</label>
                  <select
                    value={posteMode}
                    onChange={e => {
                      const v = e.target.value;
                      setPosteMode(v);
                      if (v === "__custom__" || v === "") setForm({ ...form, poste_id: "", poste: "" });
                      else {
                        const p = postes.find(x => x.poste_id === v);
                        setForm({ ...form, poste_id: v, poste: p?.name || v });
                      }
                    }}
                    className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="new-member-poste-select">
                    <option value="">— Aucun poste (droits par défaut) —</option>
                    {postes.map(p => (
                      <option key={p.poste_id} value={p.poste_id}>{p.name} ({(p.grants || []).length} droits)</option>
                    ))}
                    <option value="__custom__">Autre poste (saisie libre, droits par défaut)</option>
                  </select>
                  {posteMode === "__custom__" && (
                    <input
                      value={form.poste}
                      onChange={e => setForm({ ...form, poste: e.target.value })}
                      placeholder="Intitulé du poste"
                      className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white mt-1.5"
                      data-testid="new-member-poste-input"
                    />
                  )}
                  <p className="text-[10px] text-slate-500 mt-1">Les droits restent ajustables ensuite dans « Gestion des accès ».</p>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Roles metier (multi-selection)</label>
                <div className="grid grid-cols-2 gap-1.5" data-testid="new-member-business-roles">
                  {BUSINESS_ROLES.map(b => {
                    const selected = (form.business_roles || []).includes(b.value);
                    return (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => {
                          const cur = form.business_roles || [];
                          const next = selected ? cur.filter(r => r !== b.value) : [...cur, b.value];
                          setForm({ ...form, business_roles: next });
                        }}
                        className={`text-left px-2.5 py-1.5 rounded-lg border text-[11px] transition-all ${selected ? "" : "hover:bg-white/5"}`}
                        style={{
                          borderColor: selected ? b.color : "rgba(255,255,255,0.08)",
                          background: selected ? `${b.color}15` : "transparent",
                          color: selected ? b.color : "#cbd5e1",
                        }}
                        data-testid={`new-member-business-role-${b.value}`}
                      >
                        <div className="flex items-center gap-1.5">
                          {selected ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded border border-white/15" />}
                          <span className="font-bold">{b.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={() => { setShowAdd(false); setError(""); }} variant="outline" className="flex-1 border-white/10">Annuler</Button>
              <Button onClick={addMember} disabled={addLoading || !form.email} className="flex-1 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="confirm-add-member">
                {addLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Ajouter
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

